import { useEffect, useRef, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { useAuth } from "../../hooks/useAuth";
import { syncFromStorage } from "../../features/auth/authSlice";
import { returnPath } from "../../features/auth/returnPath";
import { tokenHandler } from "../../utils/tokenHandler";
import { hasRole } from "../../utils/userRoles";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: number[];
}

const ProtectedRoute = ({ children, requiredRoles = [] }: ProtectedRouteProps) => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated } = useAuth();
  const navigationAttempted = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenValid = tokenHandler.isValid(0);
  // Read while rendering: by the time the effect below runs, <Navigate> has
  // already moved the browser to "/".
  const requestedPath = `${window.location.pathname}${window.location.search}`;

  useEffect(() => {
    if (navigationAttempted.current) {
      return;
    }

    if (!isAuthenticated || !tokenValid) {
      navigationAttempted.current = true;
      // A token that is present but expired is a session that ran out (a
      // bookmarked page opened the next morning), so the login returns here.
      // No token at all is the Logout button or a first visit: nothing to resume.
      if (tokenHandler.get() && !tokenValid) {
        returnPath.save(requestedPath);
      }
      timeoutRef.current = setTimeout(() => {
        if (!tokenValid) {
          tokenHandler.handleExpired();
          dispatch(syncFromStorage());
        }
      }, 100);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dispatch, isAuthenticated, tokenValid]);

  if (!isAuthenticated || !tokenValid) {
    return <Navigate to="/" replace />;
  }

  // Bypassed: allow all pages to all users
  // if (requiredRoles.length > 0 && !hasRole(user, requiredRoles)) {
  //   return <Navigate to="/unauthorized" replace />;
  // }

  return <>{children}</>;
};

export default ProtectedRoute;
